<template>
  <div class="d-flex flex-column rounded pb-4 card elevation-3 flex-grow-1">
    <div
      class="
        program-badge
        success
        white--text
        d-flex
        align-center
        justify-center
        text-center
        body-2
        elevation-1
      "
      v-if="program.isDraft"
    >
      Draft
    </div>

    <div class="image-wrapper d-flex flex-column justify-center rounded-t mb-4">
      <v-icon x-large color="black"> mdi-eye-off </v-icon>
      <div class="body-1 text-center">{{ $t('noImageAvailable') }}</div>
    </div>

    <v-divider class="mb-2"></v-divider>

    <div class="subtitle-1 font-weight-medium text-center">
      {{ program.defaultTitle }}
    </div>

    <div
      class="subtitle-2 error--text text--lighten-1 text-center"
      v-if="program.testingMode"
    >
      {{ ($t('trialPeriodActive')) }}
    </div>

    <div
      class="body-2 text-center mb-1 error--text text--lighten-1"
      v-if="program.testingMode"
    >
      {{ $t('remaining') }}
      {{
        testProgramHours != 1
          ? `${testProgramHours} ${$t('hours')}`
          : `${testProgramHours} ${$t('hour')}`
      }}
      {{ $t('and') }}
      {{
        testProgramMinutes != 1
          ? `${testProgramMinutes} ${$t('minutes')}`
          : `${testProgramMinutes} ${$t('minute')}`
      }}
    </div>

    <div class="d-flex justify-center mt-auto pt-2">
      <v-btn
        color="warning lighten-1"
        small
        :lang="$i18n.locale"
        @click="onEditProgram(program.uniqueCode)"
      >
        <v-icon left>mdi-pencil</v-icon>
        {{ $t('edit') }}
      </v-btn>

      <v-btn
        v-if="role == 'superuser'"
        class="ml-2"
        small
        depressed
        color="error lighten-1"
        :lang="$i18n.locale"
        @click="onEditProgram(program.uniqueCode, true)"
      >
        {{ $t('test') }}
      </v-btn>
    </div>

    <div
      v-if="role == 'admin'" 
      class="d-flex justify-center mt-2">
      <v-btn
        v-if="program.isDraft"
        color="error"
        class="ml-2"
        small
        :lang="$i18n.locale"
        @click="onOpenDeleteDraftProgramDialog(program)"
      >
        <v-icon left>mdi-delete</v-icon>
        {{ $t('delete') }}
      </v-btn>
    </div>
    
    <div
      v-else-if="role == 'superuser'" 
      class="d-flex justify-center mt-2">
      <v-btn
        color="info"
        class="ml-2"
        small
        :lang="$i18n.locale"
        :loading="copyLoader"
        @click="onCreateCopy(program)"
      >
        <v-icon left>mdi-content-copy</v-icon>
        {{ $t('createCopy') }}
      </v-btn>

      <v-btn
        color="error"
        class="ml-2"
        small
        :lang="$i18n.locale"
        @click="onOpenDeleteProgramDialog(program)"
      >
        <v-icon left>mdi-delete</v-icon>
        {{ $t('delete') }}
      </v-btn>
    </div>

    <v-dialog v-model="dialog.open" max-width="500px">
      <div class="white pa-5" v-if="dialog.open">
        <div class="body-1 text-center mb-4">
          {{ $t('deleteDraft') }} <b>{{ dialog.program.defaultTitle }}</b
          >;
        </div>

        <div class="d-flex justify-center">
          <v-btn
            small
            class="mr-2"
            @click="dialog.open = false"
            :lang="$i18n.locale"
          >
            {{ $t('cancel') }}
          </v-btn>

          <v-btn
            small
            color="error"
            @click="onDeleteDraft"
            :loading="loader"
            :lang="$i18n.locale"
          >
            {{ $t('delete') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="dialogProgram.open" max-width="500px">
      <div class="white pa-5" v-if="dialogProgram.open">
        <div class="body-1 text-center mb-4">
          {{ $t('deleteProgram') }} <b>{{ dialogProgram.program.defaultTitle }}</b
          >;
        </div>

        <div class="d-flex justify-center">
          <v-btn
            small
            class="mr-2"
            @click="dialogProgram.open = false"
            :lang="$i18n.locale"
          >
            {{ $t('cancel') }}
          </v-btn>

          <v-btn
            small
            color="error"
            @click="onDeleteProgram"
            :loading="loader"
            :lang="$i18n.locale"
          >
            {{ $t('delete') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import moment from "moment";
import axios from 'axios';

export default {
  props: ["program"],
  data() {
    return {
      dialog: {
        open: false,
        program: null
      },
      dialogProgram: {
        open: false,
        program: null,
      },
      loader: false,
      copyLoader: false,
      testProgramHours: 0,
      testProgramMinutes: 0,
    };
  },
  computed: {
    ...mapState({
      role: (state) => state.auth.role,
      activeMuseum: (state) => state.superuser.activeMuseum,
      currentProgram: (state) => state.admin.currentProgram,
      currentSection: (state) => state.admin.currentSection
    }),
  },
  created() {
    if (this.program.testingMode) {
      this.testProgramHours = moment()
        .startOf("day")
        .seconds(this.program.remainingTestingTimeInSeconds)
        .format("H");

      this.testProgramMinutes = moment()
        .startOf("day")
        .seconds(this.program.remainingTestingTimeInSeconds)
        .format("m");
    }
  },
  methods: {
    ...mapActions([
      "deleteDraft",
      "deleteProgram",
      "getPrograms",
      "newProgram",
      "newSection",
      "newAsset"
    ]),
    onOpenDeleteDraftProgramDialog(program) {
      this.dialog = {
        open: true,
        program,
      };
    },
    async onDeleteDraft() {
      this.loader = true;

      try {
        await this.deleteDraft(this.dialog.program.id);
        await this.getPrograms();
      } catch (e) {
        console.log(e);
      }

      this.loader = false;
      this.dialog.open = false;
    },
    onOpenDeleteProgramDialog(program){
      this.dialogProgram = {
        open: true,
        program,
      };
    },
    async onDeleteProgram() {
      this.loader = true;

      try {
        await this.deleteProgram(this.dialogProgram.program.id);
        await this.getPrograms();
      } catch (e) {
        console.log(e);
      }

      this.loader = false;
      this.dialog.open = false;
    },
    onEditProgram(uniqueCode, trial = false) {
      if (this.role == "admin") {
        this.$router.push({
          path: `/program/edit/${uniqueCode}/step1`,
        });
      } else {
        this.$router.push({
          path: `/superuser/museum/${
            this.activeMuseum.id
          }/program/edit/${uniqueCode}/${trial ? "trial" : "step1"}`,
        });
      }
    },
    async onCreateCopy(program) {
      let programLocales = [];
      
      if (program.programLocales.length === 0) {
        programLocales.push({locale: program.defaultLocale, title: program.defaultTitle})
      } else {
        programLocales = program.programLocales
      }
      
      let data = {
        defaultLocale: program.defaultLocale,
        defaultTitle: program.defaultTitle + '_copy',
        uniqueCode: program.uniqueCode + '_copy',
        mainPhoto: program.mainPhoto,
        active: true,
        draft: false,
        testingMode: program.testingMode,
        remainingTestingTimeInSeconds: program.remainingTestingTimeInSeconds,
        museumId: this.activeMuseum.id,
        description: '',
        locales: programLocales
      };

      this.copyLoader = true;
      try {
        // First, create the duplicate of the program
        await this.newProgram(data);
        await this.getPrograms();

        const newProgramId = this.currentProgram.id;

        // Then, create duplicates of all the program's sections sequentally
        for (const section of program.sections) {
          let sectionLocales = [];
          if (section.sectionLocales.length === 0) {
            sectionLocales.push({locale: section.defaultLocale, title: section.defaultTitle})
          } else {
            sectionLocales = section.sectionLocales
          }

          const sectionData = {
            defaultLocale: section.defaultLocale,
            defaultTitle: section.defaultTitle,
            programId: newProgramId,
            ordering: section.ordering,
            locales: sectionLocales
          };

          await this.newSection(sectionData);

          // After the create of each section, create duplicates of all its assets
          if (section.assets && section.assets.length > 0) {
            for (const asset of section.assets) {
              const formData = new FormData();
              formData.set("sectionId", this.currentSection.id);
              formData.set("type", asset.type);
              formData.set("ordering", asset.ordering);
              formData.set("presentationInclude", asset.presentationInclude);

              if (asset.type === 'LINK') {
                formData.set("hasSound", asset.hasSound);
                formData.set("link", asset.storedPath);
              } else {
                let assetUrl = `${process.env.VUE_APP_BASE_URL}/assets/serve/${asset.storedPath}`;
                const fileResponse = await axios.get(assetUrl, {responseType: 'blob'});

                const blob = fileResponse.data;
                const extension = blob.type.split('/')[1] || 'bin';
                const filename = `asset-${asset.id}.${extension}`;

                const file = new File([blob], filename, {
                  type: fileResponse.data.type
                });
                formData.set("assetFile", file);
              }
              let newAssetUrl = `${process.env.VUE_APP_BASE_URL}/assets/`;

              await axios.post(newAssetUrl, formData);
            }
          }
        }

        await this.getPrograms();
      } catch (e) {
        console.log(e)
        this.copyLoader = false;
      } finally {
        this.copyLoader = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.card {
  border: 1px solid #e0e0e0;
  background-color: #fafafa;
  position: relative;
}

.image-wrapper {
  height: 200px;
}

.program-badge {
  position: absolute;
  top: -15px;
  right: -15px;
  height: 40px;
  width: 40px;
  border-radius: 50%;
}
</style>
